import { type ZodType } from 'zod'
import { stripGAItemParams } from '@design-system/utils/googleAnalyticsEcommerce'
/**
 * A composable for sending Google Analytics 4 (GA4) events with validation and optional configurations.
 *
 * @template TEvent - Generic type representing the event structure
 * @param {ZodType<TEvent>} schema - Zod schema for validating the event structure
 * @param {TEvent} event - The event object to be sent to Google Tag Manager
 * @param {Object} [options] - Optional configuration for event sending
 * @param {boolean} [options.clearEcommerce=false] - Whether to clear previous ecommerce data before sending
 * @param {boolean} [options.debug=false] - Enable debug logging of events
 * @param {boolean} [options.waitForDatalayerSetup=false] - Wait for datalayer setup before sending event
 * @param {number} [options.maxDatalayerSetupWait] Sets a maximum time where the event fires even if the DL_Setup event hasn't fired yet
 * @returns {void}
 * @throws {Error} Logs an error if event validation fails
 * @remarks CLIENT REQUEST: when `ga4.stripItemParams` is on, agreed item params are stripped from `ecommerce.items[]` just before the push.
 * @example
 * // Send a simple page view event
 * useGASendEvent(pageViewSchema, {
 *   event: 'page_view',
 *   page_location: window.location.href
 * });
 *
 * // Send an event with debug mode and ecommerce clearing
 * useGASendEvent(purchaseSchema, purchaseEvent, {
 *   clearEcommerce: true,
 *   debug: true
 * });
 */
export const useGASendEvent = <TEvent extends Record<string, any>>(
  schema: ZodType<TEvent>,
  event: TEvent,
  options?: {
    clearEcommerce?: boolean
    debug?: boolean
    waitForDatalayerSetup?: boolean // wait for datalayer_setup event
    maxDatalayerSetupWait?: number
  }
) => {
  // Check if GTM-ID is present
  const config = useRuntimeConfig()
  if (!config.public.scripts.googleTagManager.id) return
  if (!import.meta.client) return

  const { dataLayer } = useScriptGoogleTagManager()
  const isValid = schema.safeParse(event).success

  if (!isValid) {
    console.error(
      `Nuxt GA4 - Wrong validation: ${event?.event ?? ''}` +
        schema.safeParse(event).error
    )
    return
  }

  if (options?.debug) {
    console.log('Nuxt GA4 - debug event', {
      ...event,
    })
  }

  const nuxt = useNuxtApp()

  const sendEvent = () => {
    if (options?.clearEcommerce) dataLayer.push({ ecommerce: null })
    // CLIENT REQUEST: strip AFTER schema validation (the schemas still require these
    // keys) and without mutating `event` (item objects are reused across events).
    dataLayer.push(
      config.public.ga4.stripItemParams ? stripGAItemParams(event) : event
    )
  }

  if (!options?.waitForDatalayerSetup) {
    sendEvent()
    return
  }

  const currentPath = nuxt.$router.currentRoute.value.path
  const dataLayerSetupEventAlreadySent = nuxt._dlSetupLastPath === currentPath

  if (dataLayerSetupEventAlreadySent) {
    sendEvent()
    return
  }

  // fallback in case the datalayer setup hook never fires. Opt-in
  const { start: startMaxWait, stop: stopMaxWait } =
    options.maxDatalayerSetupWait
      ? useTimeoutFn(
          () => {
            console.error(
              'Nuxt GA4 - maxDatalayerSetupWait elapsed without DL setup, pushing anyway:',
              event.event
            )
            stopHook()
            sendEvent()
          },
          options.maxDatalayerSetupWait,
          { immediate: false }
        )
      : { start: () => {}, stop: () => {} }

  // wait for the datalayer setup hook
  const stopHook = nuxt.hook('armani_dl_setup-event-fired', () => {
    if (options.maxDatalayerSetupWait) stopMaxWait()
    sendEvent()
    stopHook()
  })

  if (options.maxDatalayerSetupWait) startMaxWait()
}
